import StarRating from "../admin/components/StarRating";
import React, { useEffect, useMemo, useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Badge, Button, Card, Container, Spinner, Table } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { fetchOwnerTripTracking, fetchTransportOwnerPortal } from "../services/transportMasterApi";
import { ownerEpassFileUrl, uploadOwnerEpass } from "../services/epassApi";
import { formatDateDMY, formatDateTimeDMY, formatDateTimeDMY12 } from "../utils/dateFormat";
import {
  EPASS_ACCEPT,
  EPASS_NOT_NEEDED,
  EPASS_UPLOADED,
  epassFileProblem,
  epassStatusLabel,
  epassStatusVariant,
} from "../utils/epassStatus";
import { buildBookingRows, TimelineModal, TrackingSummary } from "../admin/modules/tracking/TripTrackingNew";

export default function TransportOwnerPortal() {
  const { accessKey } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [error, setError] = useState("");

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const response = await fetchTransportOwnerPortal(accessKey);
      setData(response);
      if (selectedBooking) {
        const rows = buildOwnerTrackingRows(response);
        setSelectedBooking(rows.find((booking) => booking.bookingId === selectedBooking.bookingId) || selectedBooking);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Owner portal link is invalid or inactive.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessKey]);

  const bookings = useMemo(() => buildOwnerTrackingRows(data), [data]);
  const groupedBookings = useMemo(() => ({
    ongoing: buildOwnerTrackingRows(data, "ongoing"),
    upcoming: buildOwnerTrackingRows(data, "upcoming"),
  }), [data]);
  // The tracking rows are rebuilt field by field, so e-passes and the amount the driver collects
  // travel alongside, by booking.
  const tripsByBooking = useMemo(() => {
    const trips = data?.trips || {};
    return new Map([...(trips.ongoing || []), ...(trips.upcoming || [])]
      .map((trip) => [trip.bookingId, trip]));
  }, [data]);
  const tripProps = {
    accessKey,
    tripsByBooking,
    onEpassUploaded: () => load({ silent: true }),
  };

  const refreshTimeline = async () => {
    setRefreshing(true);
    try {
      await load({ silent: true });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return <Container className="py-5 text-center"><Spinner /><div className="small text-muted mt-2">Loading trips...</div></Container>;
  }

  if (error) {
    return <Container className="py-5"><Card body className="text-danger">{error}</Card></Container>;
  }

  const owner = data?.owner || {};
  return (
    <Container fluid className="py-3 px-2 px-md-4 bg-light min-vh-100">
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-start gap-2">
          <div>
            <h4 className="fw-bold mb-1">Trip Tracking</h4>
            <div className="text-muted">{owner.name || "Transport Owner"} {owner.phone ? `| ${owner.phone}` : ""}</div>
          </div>
          <Button size="sm" variant="outline-primary" onClick={() => load({ silent: true })}>Refresh</Button>
        </Card.Body>
      </Card>

      <DriverLoanBalances balances={data?.driverLoanBalances} />

      <Card className="shadow-sm border-0">
        <Card.Header className="fw-semibold">Trips ({bookings.length})</Card.Header>
        <Card.Body>
          {!bookings.length ? (
            <div className="text-muted py-4 text-center">No assigned trips found.</div>
          ) : (
            <>
              <TripSection title="Ongoing" rows={groupedBookings.ongoing} emptyText="No ongoing trips." onView={setSelectedBooking} {...tripProps} />
              <TripSection title="Upcoming" rows={groupedBookings.upcoming} emptyText="No upcoming trips." onView={setSelectedBooking} {...tripProps} />
            </>
          )}
          <CompletedLedger rows={data?.completedTrips || []} />
        </Card.Body>
      </Card>

      <TimelineModal
        booking={selectedBooking}
        onHide={() => setSelectedBooking(null)}
        onRefresh={refreshTimeline}
        refreshing={refreshing}
        showPublicActions={false}
        variant="owner"
        loadDetail={(bookingId) => fetchOwnerTripTracking(accessKey, bookingId)}
      />
    </Container>
  );
}

// Balances only - what each driver still owes. The transactions stay in the admin screen.
function DriverLoanBalances({ balances }) {
  const rows = balances || [];
  if (!rows.length) return null;
  const outstanding = rows.reduce((total, row) => total + Number(row.outstanding || 0), 0);
  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Header className="fw-semibold d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>Driver Advances</span>
        <Badge bg="warning" text="dark">Outstanding ₹{outstanding.toLocaleString("en-IN")}</Badge>
      </Card.Header>
      <Card.Body>
        <div className="small text-muted mb-2">
          Goodwill advances given to your drivers. Interest free and separate from trip payments.
        </div>
        <div className="table-responsive">
          <Table hover size="sm" className="align-middle bg-white mb-0">
            <thead>
              <tr>
                <th>Driver</th>
                <th className="text-end">Given</th>
                <th className="text-end">Repaid</th>
                <th className="text-end">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.driverId}>
                  <td>
                    <div className="fw-semibold">{row.driverName}</div>
                    <div className="small text-muted">{row.driverPhone}</div>
                  </td>
                  <td className="text-end">₹{Number(row.totalLent || 0).toLocaleString("en-IN")}</td>
                  <td className="text-end text-success">₹{Number(row.totalRepaid || 0).toLocaleString("en-IN")}</td>
                  <td className="text-end fw-semibold text-danger">₹{Number(row.outstanding || 0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

function buildOwnerTrackingRows(data, groupName = null) {
  const trips = data?.trips || {};
  const sourceTrips = groupName ? (trips[groupName] || []) : [...(trips.ongoing || []), ...(trips.upcoming || [])];
  const bookingRows = sourceTrips
    .map((trip) => ({
      id: trip.bookingId,
      tripId: trip.tripId,
      tripTitle: trip.tripTitle,
      travelDate: trip.travelDate,
      pickupDateTime: trip.pickupDateTime,
      dropDateTime: trip.dropDateTime,
      tripStatus: trip.tripStatus,
      noOfTravellers: trip.noOfTravellers,
      userName: trip.customerName,
      customerName: trip.customerName,
      userPhone: trip.customerPhone,
      customerPhone: trip.customerPhone,
      plannedByName: trip.plannedByName,
      plannedByPhone: trip.plannedByPhone,
      driverId: trip.driverId,
      driverName: trip.driverName,
      driverPhone: trip.driverPhone,
      vehicleName: trip.vehicleName,
      vehicleRegistrationNumber: trip.vehicleRegistrationNumber,
      assignedVehicleName: trip.vehicleName,
      assignedVehicleRegistrationNumber: trip.vehicleRegistrationNumber,
      trackingPublicToken: trip.trackingPublicToken,
      driverGpsTestDone: trip.driverGpsTestDone,
      driverGpsLatitude: trip.driverGpsLatitude,
      driverGpsLongitude: trip.driverGpsLongitude,
      driverGpsAccuracy: trip.driverGpsAccuracy,
      driverGpsTestedAt: trip.driverGpsTestedAt,
      driverPracticeCheckDone: trip.driverPracticeCheckDone,
      driverPracticeCheckedAt: trip.driverPracticeCheckedAt,
      driverReadinessDeviceInfo: trip.driverReadinessDeviceInfo,
      transportPayable: trip.transportPayable,
      transportPaid: trip.transportPaid,
      transportBalance: trip.transportBalance,
    }));
  return buildBookingRows(bookingRows, data?.trackingRows || []);
}

// Finished trips, as a payment ledger - the date and which of their vehicles ran it (by name, no
// registration) so a payment can be matched, but no driver, no guest, no route, no tracking. A
// driver introduced by an owner may later work with us directly, and the owner should not be able
// to read that history back months afterwards.
function CompletedLedger({ rows }) {
  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Completed Trips</h6>
        <span className="small text-muted">Last 90 days</span>
      </div>
      {!rows.length ? (
        <div className="text-muted small py-3">No completed trips in the last 90 days.</div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle bg-white mb-0">
            <thead>
              <tr>
                <th style={{ width: 120 }}>Booking</th>
                <th style={{ width: 130 }}>Date</th>
                <th>Trip</th>
                <th>Vehicle</th>
                <th className="text-end" style={{ width: 160 }}>Paid</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.bookingId}>
                  <td className="text-nowrap fw-semibold">#{row.bookingId}</td>
                  <td className="text-nowrap">{formatDateDMY(row.travelDate, "-")}</td>
                  <td>{row.tripTitle || "Trip"}</td>
                  <td>{row.vehicleName || "-"}</td>
                  <td className="text-end text-success fw-semibold text-nowrap">
                    {formatCurrency(row.transportPaid)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

// How IO Trips rated this trip. Only shown once somebody has actually rated it - an unrated trip
// is not a zero-star trip.
function TripRating({ booking }) {
  const driver = booking.driverRating;
  const vehicle = booking.vehicleRating;
  if (!driver && !vehicle) return <span className="small text-muted">-</span>;
  return (
    <div className="small">
      {driver ? (
        <div className="d-flex align-items-center gap-1">
          <span className="text-muted">Driver</span>
          <StarRating value={driver} size="0.8rem" label={`Driver rated ${driver} of 5`} />
        </div>
      ) : null}
      {vehicle ? (
        <div className="d-flex align-items-center gap-1">
          <span className="text-muted">Vehicle</span>
          <StarRating value={vehicle} size="0.8rem" label={`Vehicle rated ${vehicle} of 5`} />
        </div>
      ) : null}
      {(booking.driverRatingNote || booking.vehicleRatingNote) && (
        <div className="text-muted fst-italic" style={{ maxWidth: 220, whiteSpace: "normal" }}>
          {[booking.driverRatingNote, booking.vehicleRatingNote].filter(Boolean).join(" · ")}
        </div>
      )}
    </div>
  );
}

function TripSection({ title, rows, emptyText, onView, accessKey, tripsByBooking, onEpassUploaded }) {
  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="fw-bold mb-0">{title}</h6>
        <Badge bg="light" text="dark">{rows.length}</Badge>
      </div>
      {!rows.length ? (
        <div className="text-muted small border rounded bg-white p-3">{emptyText}</div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle bg-white mb-0">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>Booking</th>
                <th>Trip</th>
                <th>Customer</th>
                <th>Pickup</th>
                <th>Drop</th>
                <th>Driver</th>
                <th>Vehicle</th>
                <th>E-pass</th>
                <th>Driver Collects</th>
                <th>Payment</th>
                <th>Rating</th>
                <th>Readiness</th>
                <th>Tracking</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((booking, index) => (
                <tr key={booking.bookingId}><td className="small fw-semibold">{index + 1}</td>
                  <td className="text-nowrap">
                    #{booking.bookingId}
                    <div><Badge bg={statusVariant(booking.tripStatus)}>{booking.tripStatus || "PENDING"}</Badge></div>
                  </td>
                  <td>
                    <div className="fw-semibold">{booking.tripTitle || "Trip"}</div>
                    <div className="small text-muted">Travel: {formatDateDMY(booking.travelDate, "-")}</div>
                  </td>
                  <td>
                    <div className="fw-semibold">{booking.customerName || booking.userName || "-"}</div>
                    <CustomerPhone phone={booking.customerPhone || booking.userPhone} />
                    {booking.plannedByPhone && (
                      <div className="small text-muted">Planned by: {booking.plannedByName || "-"} | {booking.plannedByPhone}</div>
                    )}
                  </td>
                  <td className="text-nowrap">{formatDateTimeDMY(booking.pickupDateTime, "-")}</td>
                  <td className="text-nowrap">{formatDateTimeDMY(booking.dropDateTime, "-")}</td>
                  <td>
                    <div>{booking.driverName || "-"}</div>
                    <div className="small text-muted">{booking.driverPhone || ""}</div>
                  </td>
                  <td>
                    <div>{booking.vehicleName || booking.assignedVehicleName || "-"}</div>
                    <div className="small text-muted">{booking.vehicleRegistrationNumber || booking.assignedVehicleRegistrationNumber || ""}</div>
                  </td>
                  <td>
                    <OwnerEpasses
                      portalKey={accessKey}
                      bookingId={booking.bookingId}
                      epasses={tripsByBooking?.get(booking.bookingId)?.epasses}
                      onUploaded={onEpassUploaded}
                    />
                  </td>
                  <td className="text-nowrap">
                    <DriverCollects amount={tripsByBooking?.get(booking.bookingId)?.driverCollectAmount} />
                  </td>
                  <td className="text-nowrap">
                    <div className="small text-success fw-semibold">Paid {formatCurrency(booking.transportPaid)}</div>
                  </td>
                  <td className="text-nowrap"><TripRating booking={booking} /></td>
                  <td><ReadinessBadge booking={booking} /></td>
                  <td><TrackingSummary booking={booking} /></td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" onClick={() => onView(booking)}>View Timeline</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

// The same amount the driver's message gives: only what to collect and when, never how it is worked
// out. Nothing is shown for bookings sold before the payment schedule.
function DriverCollects({ amount }) {
  if (amount == null) return <span className="small text-muted">-</span>;
  if (Number(amount) <= 0) return <Badge bg="success">Received</Badge>;
  return (
    <div>
      <div className="fw-semibold text-danger">{formatCurrency(amount)}</div>
      <div className="small text-muted">After boarding · paid to IO Trips</div>
    </div>
  );
}

// E-passes the trip needs: the owner applies with the vehicle number and uploads it here, so staff
// and the driver both have it. Replacing is allowed; removing is left to IO Trips.
function OwnerEpasses({ portalKey, bookingId, epasses, onUploaded }) {
  const [busy, setBusy] = useState(null);
  const shown = (epasses || []).filter((epass) => epass.status !== EPASS_NOT_NEEDED);
  if (!shown.length) return <span className="small text-muted">-</span>;

  const upload = async (epass, file) => {
    const problem = epassFileProblem(file);
    if (problem) {
      alert(problem);
      return;
    }
    setBusy(epass.epassId);
    try {
      await uploadOwnerEpass(portalKey, bookingId, epass.epassId, file);
      await onUploaded();
    } catch (err) {
      alert(err?.response?.data?.message || "Could not upload the e-pass. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="small d-flex flex-column gap-2" style={{ minWidth: 200 }}>
      {shown.map((epass) => (
        <div key={epass.epassId}>
          <div className="fw-semibold">{epass.name}</div>
          <div className="text-muted">Needed on {formatDateDMY(epass.neededOn, "-")}</div>
          <Badge bg={epassStatusVariant(epass.status)}>{epassStatusLabel(epass.status)}</Badge>
          {epass.status !== EPASS_UPLOADED && epass.instructions && (
            <div className="text-muted mt-1" style={{ whiteSpace: "pre-line", maxWidth: 260 }}>{epass.instructions}</div>
          )}
          <div className="d-flex flex-wrap gap-1 mt-1">
            {epass.status !== EPASS_UPLOADED && epass.applyLink && (
              <a className="btn btn-sm btn-outline-primary py-0 px-2" href={epass.applyLink} target="_blank" rel="noreferrer">Apply</a>
            )}
            <label className={`btn btn-sm btn-outline-primary py-0 px-2 mb-0${busy === epass.epassId ? " disabled" : ""}`}>
              {busy === epass.epassId ? <Spinner size="sm" animation="border" /> : epass.fileName ? "Replace" : "Upload"}
              <input
                type="file"
                hidden
                accept={EPASS_ACCEPT}
                disabled={busy === epass.epassId}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) upload(epass, file);
                }}
              />
            </label>
            {epass.fileName && (
              <>
                <a className="btn btn-sm btn-outline-secondary py-0 px-2" href={ownerEpassFileUrl(portalKey, bookingId, epass.epassId)} target="_blank" rel="noreferrer">View</a>
                <a className="btn btn-sm btn-outline-secondary py-0 px-2" href={ownerEpassFileUrl(portalKey, bookingId, epass.epassId, true)}>Download</a>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CustomerPhone({ phone }) {
  if (!phone) return <div className="small text-muted">-</div>;
  const callablePhone = String(phone).trim().replace(/[^\d+]/g, "");
  return (
    <div className="d-flex align-items-center gap-1 small">
      <span>{phone}</span>
      {callablePhone ? (
        <a
          href={`tel:${callablePhone}`}
          className="btn btn-sm btn-outline-success d-inline-flex align-items-center justify-content-center p-1"
          aria-label={`Call ${phone}`}
          title={`Call ${phone}`}
        >
          <i className="bi bi-telephone-fill" aria-hidden="true" />
        </a>
      ) : null}
    </div>
  );
}

function ReadinessBadge({ booking }) {
  const gpsDone = Boolean(booking.driverGpsTestDone);
  const practiceDone = Boolean(booking.driverPracticeCheckDone);
  const ready = gpsDone && practiceDone;
  return (
    <div>
      <Badge bg={ready ? "success" : "warning"} text={ready ? undefined : "dark"}>
        {ready ? "Driver Ready" : gpsDone ? "Practice Pending" : "GPS Pending"}
      </Badge>
      {booking.driverGpsTestedAt ? (
        <div className="small text-muted mt-1">GPS: {formatDateTimeDMY12(booking.driverGpsTestedAt, "-")}</div>
      ) : null}
    </div>
  );
}

function formatCurrency(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function statusVariant(status) {
  if (status === "COMPLETED") return "success";
  if (status === "ONGOING") return "warning";
  if (status === "UPCOMING") return "primary";
  return "secondary";
}
